import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Briefcase, FileText, Clock, CheckCircle, Phone, Mail, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CaseChatThread from '../components/CaseChatThread';

const STATUS_OPTIONS = ['new', 'documents_pending', 'under_review', 'filed', 'completed', 'cancelled'];

const ConsultantPortalPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docsByCaseId, setDocsByCaseId] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: '', admin_notes: '', quoted_amount: '' });

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    if (user.role !== 'tax_consultant' && user.role !== 'admin') {
      toast.error('Tax consultant access required');
      navigate('/');
      return;
    }
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCases = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
    try {
      const res = await axios.get(`${API}/consultant/cases`, { headers });
      setCases(res.data || []);
      // Fetch documents per case in parallel
      const docPromises = (res.data || []).map((c) =>
        axios.get(`${API}/tax-cases/${c.id}/documents`, { headers }).then((r) => [c.id, r.data]).catch(() => [c.id, []])
      );
      const docResults = await Promise.all(docPromises);
      const docMap = {};
      docResults.forEach(([id, docs]) => { docMap[id] = docs; });
      setDocsByCaseId(docMap);
    } catch (e) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const downloadDoc = async (doc) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
    try {
      const res = await axios.get(`${API}/tax-documents/${doc.id}/download`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const save = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` };
    try {
      await axios.patch(`${API}/consultant/cases/${editing.id}`, {
        status: form.status || null,
        admin_notes: form.admin_notes || null,
        quoted_amount: form.quoted_amount ? Number(form.quoted_amount) : null,
      }, { headers });
      toast.success('Case updated');
      setEditing(null);
      fetchCases();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const openCases = cases.filter((c) => !['completed', 'cancelled'].includes(c.status));
  const closedCases = cases.filter((c) => ['completed', 'cancelled'].includes(c.status));

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }} data-testid="consultant-portal">
      <Navbar />
      <section style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', padding: '3rem 1.5rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Briefcase size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', color: '#0F172A', margin: 0, fontWeight: 800 }}>Tax Consultant Portal</h1>
              <p style={{ color: '#475569', margin: '0.25rem 0 0' }}>Welcome, {user?.full_name?.split(' ')[0] || 'Consultant'} — {openCases.length} open case{openCases.length === 1 ? '' : 's'}</p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem 1.5rem 4rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }} data-testid="consultant-loading"><Loader2 size={20} className="animate-spin" /> Loading...</div>
          ) : cases.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: '1.5rem', border: '1px solid #E2E8F0' }} data-testid="consultant-no-cases">
              <Receipt size={64} style={{ color: '#E2E8F0', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#0F172A' }}>No cases assigned yet</h3>
              <p style={{ color: '#475569' }}>The admin will assign tax cases to you. They'll appear here.</p>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '1rem' }}>Open Cases ({openCases.length})</h2>
              <div style={{ display: 'grid', gap: '1rem', marginBottom: '2.5rem' }} data-testid="consultant-open-cases">
                {openCases.map((c) => (
                  <CaseCard key={c.id} c={c} docs={docsByCaseId[c.id] || []} onDownload={downloadDoc} onEdit={() => { setEditing(c); setForm({ status: c.status, admin_notes: c.admin_notes || '', quoted_amount: c.quoted_amount || '' }); }} />
                ))}
                {openCases.length === 0 && <p style={{ color: '#94A3B8', padding: '1rem' }}>No open cases right now 🎉</p>}
              </div>

              {closedCases.length > 0 && (
                <>
                  <h2 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '1rem' }}>Completed Cases ({closedCases.length})</h2>
                  <div style={{ display: 'grid', gap: '1rem' }} data-testid="consultant-closed-cases">
                    {closedCases.map((c) => (
                      <CaseCard key={c.id} c={c} docs={docsByCaseId[c.id] || []} onDownload={downloadDoc} onEdit={() => { setEditing(c); setForm({ status: c.status, admin_notes: c.admin_notes || '', quoted_amount: c.quoted_amount || '' }); }} closed />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }} onClick={() => setEditing(null)}>
          <div style={{ background: 'white', borderRadius: '1.25rem', padding: '1.75rem', maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()} data-testid="consultant-edit-modal">
            <h3 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '0.25rem' }}>Update Case</h3>
            <p style={{ color: '#475569', marginBottom: '1.25rem', fontSize: '0.85rem' }}>{editing.name} — {editing.service_type}</p>
            <div style={{ marginBottom: '0.85rem' }}>
              <label className="label-text">Status</label>
              <select className="input-field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="consultant-status">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label className="label-text">Quoted Amount (₹)</label>
              <input className="input-field" type="number" value={form.quoted_amount} onChange={(e) => setForm({ ...form, quoted_amount: e.target.value })} placeholder="e.g., 1499" data-testid="consultant-quoted" />
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label className="label-text">Expert notes (visible to customer)</label>
              <textarea className="input-field" rows={3} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} placeholder="e.g., All documents received. Filing tomorrow." data-testid="consultant-notes" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditing(null)} className="outline-button" style={{ flex: 1 }}>Cancel</button>
              <button onClick={save} className="primary-button" style={{ flex: 1, justifyContent: 'center', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }} data-testid="consultant-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CaseCard = ({ c, docs, onDownload, onEdit, closed }) => (
  <div style={{ background: 'white', borderRadius: '1rem', padding: '1.25rem', border: '1px solid #E2E8F0', opacity: closed ? 0.85 : 1 }} data-testid={`consultant-case-${c.id}`}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
      <div style={{ flex: 1, minWidth: 250 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span className={`status-badge status-${c.status === 'completed' || c.status === 'filed' ? 'approved' : c.status === 'cancelled' ? 'rejected' : 'pending'}`}>{c.status.replace('_', ' ')}</span>
          <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Ref: <span style={{ fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{c.id.slice(0, 8)}</span></span>
        </div>
        <h3 style={{ fontSize: '1.1rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>{c.name}</h3>
        <p style={{ color: '#475569', margin: '0.25rem 0', fontSize: '0.95rem' }}>{c.service_type}</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: 6, color: '#475569', fontSize: '0.825rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={12} /> <a href={`tel:${c.phone}`} style={{ color: '#FF6B35' }}>{c.phone}</a></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={12} /> <a href={`mailto:${c.email}`} style={{ color: '#FF6B35' }}>{c.email}</a></span>
          {c.pan && <span style={{ fontFamily: 'monospace' }}>PAN: {c.pan}</span>}
          {c.city && <span>{c.city}</span>}
        </div>
      </div>
      {!closed && <button onClick={onEdit} className="outline-button" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }} data-testid={`consultant-edit-${c.id}`}>Update</button>}
    </div>
    {c.admin_notes && (
      <div style={{ padding: '0.6rem 0.8rem', background: '#ECFDF5', borderLeft: '3px solid #10B981', borderRadius: 6, color: '#065F46', fontSize: '0.85rem', marginTop: 8 }}>
        <strong>Your note:</strong> {c.admin_notes}
      </div>
    )}
    {docs.length > 0 && (
      <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px dashed #E2E8F0' }}>
        <div style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={12} /> Customer Documents ({docs.length})
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {docs.map((d) => (
            <button key={d.id} onClick={() => onDownload(d)} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', color: '#475569', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid={`consultant-doc-${d.id}`}>
              {d.document_type}: {d.original_filename}
            </button>
          ))}
        </div>
      </div>
    )}
    {c.quoted_amount && (
      <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#475569' }}><strong>Quoted:</strong> <span style={{ color: '#10B981', fontWeight: 700 }}>₹{Number(c.quoted_amount).toLocaleString('en-IN')}</span></div>
    )}
    <CaseChatThread caseId={c.id} currentRole="tax_consultant" testIdPrefix="consultant-chat" />
  </div>
);

export default ConsultantPortalPage;
